import { toggleEnvironment, shouldUseRelativeUrls } from '../utils/apiClient';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

// API Endpoints grouped by feature
export const API_ENDPOINTS = {
  // Auth endpoints
  LOGIN: '/api/v1/stem@home/auth/login',
  REGISTER: '/api/v1/stem@home/auth/register',
  REFRESH_TOKEN: '/api/v1/stem@home/auth/refresh-token',
  RESET_PASSWORD: {
    REQUEST: '/api/v1/stem@home/auth/reset-password',
    CONFIRM: '/api/v1/stem@home/auth/reset-password/confirm'
  },
  UPDATE_PASSWORD: '/api/v1/stem@home/auth/update-password',
  CHECK_EMAIL: '/api/v1/stem@home/auth/check-email',
  // Magic link authentication endpoints
  MAGIC_LINK: {
    REQUEST: '/api/v1/stem@home/auth/request-magic-link',
    VERIFY: '/api/v1/stem@home/auth/verify-magic-link'
  },
  
  // Trial endpoints
  TRIALS: {
    CREATE: '/api/v1/stem@home/trial/create',
    VALIDATE: (code) => `/api/v1/stem@home/trial/validate/${code}`,
    STATUS: '/api/v1/stem@home/trial/status',
    UPGRADE: '/api/v1/stem@home/trial/upgrade',
    CHECK_EMAIL: '/api/v1/stem@home/trial/email-trial',
    SCHOOL_STATUS: (code) => `/api/v1/stem@home/trial/status/${code}`
  },
  
  // Student endpoints
  STUDENT: {
    ALL: '/api/v1/stem@home/student/allStudents',
    CLASSES: (userId) => `/api/v1/stem@home/student/${userId}/enrolled-classes`,
    SUBMISSIONS: '/api/v1/stem@home/student/submissions',
    ASSIGN_TO_CLASS: '/api/v1/stem@home/student/assign-students',
    ADD_TO_CLASS: (classId) => `/api/v1/stem@home/class/${classId}/add-students`,
    REMOVE_FROM_CLASS: '/api/v1/stem@home/student/remove-from-class',
    CLASS_SUBMISSIONS: (classId, studentId) => `/api/v1/stem@home/student/class/${classId}/student/${studentId}/submissions`,
    SUBMISSION_DETAILS: (submissionId) => `/api/v1/stem@home/student/submissions/${submissionId}`,
    RESULTS: (resultId) => `/api/v1/stem@home/student/results/${resultId}`,
    FEEDBACK: (feedbackId) => `/api/v1/stem@home/student/getFeedback/${feedbackId}`,
    GET_RESULT: (page) => `/api/v1/stem@home/student/getResult?page=${page}`
  },

  // Teacher endpoints
  TEACHER: {
    ALL: '/api/v1/stem@home/teacher/allTeachers',
    CLASSES: '/api/v1/stem@home/teacher/classes',
    QUESTIONS: '/api/v1/stem@home/question/questions',
    SUBMISSIONS: '/api/v1/stem@home/question/submissions',
    SCHOOL_STUDENTS: '/api/v1/stem@home/teacher/school-students',
    GET_RESULT: (page) => `/api/v1/stem@home/teacher/getResult?page=${page}`
  },

  // Class endpoints
  CLASS: {
    ALL: '/api/v1/stem@home/class/allClasses',
    CREATE: '/api/v1/stem@home/class/create',
    DETAILS: (classId) => `/api/v1/stem@home/class/${classId}`,
    QUESTIONS: (classId) => `/api/v1/stem@home/class/${classId}/questions`
  },

  // Question endpoints
  QUESTION: {
    DETAILS: (id) => `/api/v1/stem@home/questions/${id}`,
    ADD_TO_CLASS: '/api/v1/stem@home/question/questions/add-to-class',
    DELETE: (id) => `/api/v1/stem@home/questions/delete/${id}`,
    UPDATE: (id) => `/api/v1/stem@home/questions/${id}`,
    CREATE: '/api/v1/stem@home/question/questions/create'
  },

  // Admin endpoints
  ADMIN: {
    USERS: '/api/v1/stem@home/admin/users',
    CLASSES: '/api/v1/stem@home/admin/classes',
    SUBMISSIONS: '/api/v1/stem@home/admin/submissions',
    USER_DETAILS: (userId) => `/api/v1/stem@home/admin/users/${userId}`,
    USER_SUBMISSIONS: (userId) => `/api/v1/stem@home/admin/users/${userId}/submissions`
  },

  // Owner endpoints
  OWNER: {
    SCHOOLS: '/api/v1/stem@home/owner/schools',
    SCHOOL_DETAILS: (schoolId) => `/api/v1/stem@home/owner/schools/${schoolId}`,
    SCHOOL_USERS: (schoolId) => `/api/v1/stem@home/owner/schools/${schoolId}/users`,
    STATISTICS: '/api/v1/stem@home/owner/statistics',
    CREATE_ADMIN: '/api/v1/stem@home/owner/users/create-admin',
    CREATE_TEACHER: '/api/v1/stem@home/owner/users/create-teacher'
  },

  // Submission endpoints
  SUBMISSION: {
    GET: (submissionId) => `/api/v1/stem@home/student/submissions/${submissionId}`,
    LIST: '/api/v1/stem@home/student/submissions',
    CLASS: (classId, studentId) => `/api/v1/stem@home/student/class/${classId}/student/${studentId}/submissions`,
    CREATE: '/api/v1/stem@home/student/submissions/create',
    GRADE: (submissionId) => `/api/v1/stem@home/student/submissions/${submissionId}/grade`
  },

  // Payment endpoints
  PAYMENT: {
    CREATE_CHECKOUT: '/api/v1/stem@home/payment/create-checkout',
    VERIFY: '/api/v1/stem@home/payment/verify',
    SUCCESS: '/api/v1/stem@home/payment/success',
    CANCEL: '/api/v1/stem@home/payment/cancel',
    SUBSCRIPTION_CANCEL: '/api/v1/stem@home/payment/subscription/cancel',
    HISTORY: '/api/v1/stem@home/payment/history',
    SUBSCRIPTION: '/api/v1/stem@home/payment/subscription',
    CREATE_PORTAL_SESSION: '/api/v1/stem@home/payment/create-portal-session'
  },

  UPLOAD: '/api/v1/stem@home/upload',

  USERS: {
    LIST: '/api/v1/stem@home/users',
    CREATE: '/api/v1/stem@home/users/create',
    UPDATE: (id) => `/api/v1/stem@home/users/${id}`,
    DELETE: (id) => `/api/v1/stem@home/users/${id}`
  },

  CLASSES: {
    LIST: '/api/v1/stem@home/classes',
    CREATE: '/api/v1/stem@home/classes/create',
    UPDATE: (id) => `/api/v1/stem@home/classes/${id}`,
    DELETE: (id) => `/api/v1/stem@home/classes/${id}`
  },

  QUESTIONS: {
    LIST: '/api/v1/stem@home/questions',
    CREATE: '/api/v1/stem@home/questions/create',
    UPDATE: (id) => `/api/v1/stem@home/questions/${id}`,
    DELETE: (id) => `/api/v1/stem@home/questions/${id}`
  },

  SUBMISSIONS: {
    LIST: '/api/v1/stem@home/submissions',
    CREATE: '/api/v1/stem@home/submissions/create',
    UPDATE: (id) => `/api/v1/stem@home/submissions/${id}`,
    GET: (id) => `/api/v1/stem@home/submissions/${id}`
  }
};

// Helper function to get full API URL
export const getApiUrl = (endpoint) => `${API_BASE_URL}${endpoint}`;

// Helper function to get headers with auth token
export const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
}; 